import { TOOLS } from '../../links/growthPlatformsLinks';

/**
 * Response shapes of the App Store usage routes (api/src/routes/usage.ts, docs/api.md).
 * Copied here because the web can't import from api. The web never computes metrics or
 * periods: every number and period below is exactly what the server returned.
 */
export type PeriodKind = 'month' | 'quarter' | 'year';

export interface PeriodRef {
  kind: PeriodKind;
  id: string;
  label: string;
  start: string;
  end: string;
  closed: boolean;
}

export interface Metrics {
  uniques: number;
  accesses: number;
  perPerson: number;
  regulars: number;
}

export interface TargetUsage {
  /** `home`, `app:<id>` or `tool:<id>`. */
  target: string;
  current: Metrics;
  previous: Metrics;
}

export interface TrendPoint {
  /** `YYYY-MM`. */
  month: string;
  label: string;
  uniques: number;
  accesses: number;
}

export interface UsageSummary {
  period: PeriodRef;
  previous: PeriodRef;
  includeTeam: boolean;
  store: { current: Metrics; previous: Metrics };
  trend: TrendPoint[];
  targets: TargetUsage[];
  periods: PeriodRef[];
}

export interface UsagePerson {
  sub: string;
  email: string;
  name: string | null;
  accesses: number;
  daysActive: number;
  /** ISO timestamp of the last counted visit. */
  lastAt: string;
  onTeam: boolean;
}

export interface TeamMember {
  email: string;
  sub?: string;
  name?: string;
  addedBy: string;
  addedAt: string;
}

export interface Team {
  members: TeamMember[];
  updatedAt: string;
  seededAt: string;
}

/** Body of PUT /api/usage/team: no `sub` (the server looks subs up by email). */
export interface TeamBody {
  members: { email: string; name?: string }[];
}

/** The two fields of a registry entry (GET /api/registry/all) the usage page needs. */
export interface RegistryApp {
  appId: string;
  displayName: string;
}

export type Section = 'home' | 'apps' | 'tools';

export const SECTION_TITLES: Record<Section, string> = {
  home: 'App Store home',
  apps: 'Apps',
  tools: 'Growth Platforms tools',
};

const SECTION_ORDER: Section[] = ['home', 'apps', 'tools'];

/** The display name of a target, which section it belongs in, and whether its app has left the registry. */
export function targetLabel(target: string, apps: readonly RegistryApp[]): { name: string; section: Section; removed: boolean } {
  if (target === 'home') return { name: 'App Store home', section: 'home', removed: false };
  if (target.startsWith('tool:')) {
    const id = target.slice('tool:'.length);
    return { name: TOOLS.find((t) => t.id === id)?.name ?? id, section: 'tools', removed: false };
  }
  const id = target.replace(/^app:/, '');
  const app = apps.find((a) => a.appId === id);
  return { name: app?.displayName ?? id, section: 'apps', removed: !app };
}

/** "▲ 73%", "▼ 12%", "—" when unchanged, "New" when there was nothing before. */
export function changeText(cur: number, prev: number): { text: string; direction: 'up' | 'down' | 'flat' | 'new' } {
  if (prev === 0) return cur > 0 ? { text: 'New', direction: 'new' } : { text: '—', direction: 'flat' };
  const pct = Math.round((Math.abs(cur - prev) / prev) * 100);
  if (cur === prev || pct === 0) return { text: '—', direction: 'flat' };
  return cur > prev ? { text: `▲ ${pct}%`, direction: 'up' } : { text: `▼ ${pct}%`, direction: 'down' };
}

export const formatCount = (n: number) => n.toLocaleString('en-US');
export const formatPerPerson = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** The summary route's query. The first load sends neither kind nor id, so the server picks the current quarter. */
export function summaryPath(kind: PeriodKind | undefined, id: string | undefined, includeTeam: boolean): string {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (id) params.set('id', id);
  params.set('includeTeam', String(includeTeam));
  return `/api/usage/summary?${params.toString()}`;
}

export const peoplePath = (target: string, period: PeriodRef) =>
  `/api/usage/people?target=${encodeURIComponent(target)}&kind=${period.kind}&id=${encodeURIComponent(period.id)}`;

export interface TargetRow extends TargetUsage {
  name: string;
  removed: boolean;
}

/**
 * Targets grouped home, then apps, then tools, each most unique people first. Home, every app in
 * the full registry and every tool always have a row (zeros if unvisited), so an unused one shows
 * 0 instead of vanishing. A visited app that has left the registry keeps its row.
 */
export function groupTargets(targets: readonly TargetUsage[], apps: readonly RegistryApp[], registryKnown: boolean): { section: Section; rows: TargetRow[] }[] {
  const zero: Metrics = { uniques: 0, accesses: 0, perPerson: 0, regulars: 0 };
  const seen = new Set(targets.map((t) => t.target));
  const always = ['home', ...apps.map((a) => `app:${a.appId}`), ...TOOLS.map((t) => `tool:${t.id}`)];
  const all = [...targets, ...always.filter((t) => !seen.has(t)).map((target) => ({ target, current: zero, previous: zero }))];
  return SECTION_ORDER.map((section) => ({
    section,
    rows: all
      .map((t) => {
        const label = targetLabel(t.target, apps);
        return { ...t, name: label.name, section: label.section, removed: registryKnown && label.removed };
      })
      .filter((r) => r.section === section)
      .sort((a, b) => b.current.uniques - a.current.uniques || a.name.localeCompare(b.name))
      .map(({ section: _s, ...row }) => row),
  })).filter((g) => g.rows.length > 0);
}
